import { useForm } from 'react-hook-form';
import { useEffect, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { Button, Modal } from '@uniquegood/realworld-studio-design';
import StoryEditCard from './components/StoryEditCard';
import Layout from './components/Layout';
import { useModalState, usePageExit } from '@/hooks';
import { ClickEventName, PageViewEventName, track } from '@/track';

// API Response 타입 정의
interface StoryApiResponse {
  success: boolean;
  message: string;
  data: {
    title: string;
    introduction: string;
    development: string;
    conclusion: string;
    aiUseLogId: string;
  };
}

// SessionStorage에서 가져온 데이터 타입
export interface SessionStoryData {
  success: boolean;
  message: string;
  data: {
    title: string;
    introduction: string;
    development: string;
    conclusion: string;
    aiUseLogId: string;
  };
}

// React Hook Form을 위한 인터페이스
interface StoryFormData {
  title: string;
  introduction: string;
  development: string;
  conclusion: string;
  aiUseLogId: string;
}

// AiStoryConfirm 컴포넌트 Props
interface AiStoryConfirmProps {
  storyData?: StoryApiResponse['data'];
}

const emptyStoryData: StoryApiResponse['data'] = {
  title: '',
  introduction: '',
  development: '',
  conclusion: '',
  aiUseLogId: ''
};

function AiStoryConfirm({ storyData }: AiStoryConfirmProps) {
  // SessionStorage에서 데이터를 가져오는 상태
  const [sessionStoryData, setSessionStoryData] = useState<SessionStoryData | null>(null);
  // 제목 글자수 추적을 위한 상태
  const [titleLength, setTitleLength] = useState<number>(0);
  // 제목 유효성 검사를 위한 상태
  const [isTitleEmpty, setIsTitleEmpty] = useState<boolean>(false);
  // 각 섹션의 편집 상태 추적
  const [editingSections, setEditingSections] = useState<Set<string>>(new Set());
  const { appId } = useParams<AppParam>();
  const history = useHistory();
  const { modal, openModal, closeModal } = useModalState();

  // SessionStorage에서 aiStory 데이터를 로드 및 리다이렉트 확인
  useEffect(() => {
    const loadSessionStorageData = () => {
      try {
        const sessionData = sessionStorage.getItem('aiStory');

        if (!sessionData) {
          // aiStory 데이터가 없으면 메인 페이지로 리다이렉트
          allowExit();
          history.push(`/apps/${appId}`);
          return;
        }

        const parsedData: SessionStoryData = JSON.parse(sessionData);
        setSessionStoryData(parsedData);
        // 제목 글자수 초기화
        setTitleLength(parsedData.data.title.length);

        // View Event Tracking - Mixpanel
        track.onPageViewMixpanel(PageViewEventName.view_newgame_ai_storyconfirm, {
          channel_id: appId,
          title: parsedData.data.title,
          introduction: parsedData.data.introduction,
          development: parsedData.data.development,
          conclusion: parsedData.data.conclusion
        });
      } catch (error) {
        console.error('SessionStorage 데이터 로드 실패:', error);
      }
    };

    loadSessionStorageData();
  }, [appId, history]);

  // SessionStorage 데이터 우선, 없으면 props 데이터, 없으면 빈 데이터
  const activeStoryData = sessionStoryData?.data || storyData || emptyStoryData;

  // activeStoryData가 변경될 때 titleLength와 isTitleEmpty 초기화
  useEffect(() => {
    setTitleLength(activeStoryData.title.length);
    setIsTitleEmpty(activeStoryData.title.trim() === '');
  }, [activeStoryData.title]);

  // React Hook Form 설정 - 항상 호출하여 Hooks 순서 유지
  const { register, handleSubmit, watch, setValue, getValues, reset } = useForm<StoryFormData>({
    defaultValues: {
      title: activeStoryData.title,
      introduction: activeStoryData.introduction,
      development: activeStoryData.development,
      conclusion: activeStoryData.conclusion,
      aiUseLogId: activeStoryData.aiUseLogId
    }
  });

  // 세션 스토리지 데이터가 로드되면 폼을 리셋해서 새로운 값으로 채움
  useEffect(() => {
    if (sessionStoryData?.data) {
      reset({
        title: sessionStoryData.data.title,
        introduction: sessionStoryData.data.introduction,
        development: sessionStoryData.data.development,
        conclusion: sessionStoryData.data.conclusion,
        aiUseLogId: sessionStoryData.data.aiUseLogId
      });
    }
  }, [sessionStoryData, reset]);

  // 현재 폼 값들을 실시간으로 감시
  const watchedValues = watch();

  // 페이지 이탈 시 session storage 정리 함수
  const clearSessionStorage = useCallback(() => {
    sessionStorage.removeItem('aiStory');
  }, []);

  // 페이지 이탈 경고 모달 열기 함수
  const openUnsavedChangesWarningModal = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      track.onPageViewMixpanel(PageViewEventName.view_newgame_ai_exit_modal, {
        channel_id: appId
      });
      openModal({
        title: '작성한 내용이 사라질 수 있어요',
        children: (
          <div>
            콘텐츠를 만드는 도중에 페이지를 벗어나면 저장되지 않아요.
            <br />
            정말 나가시겠어요?
          </div>
        ),
        onClose: () => {
          closeModal();
          resolve(false); // 모달이 닫히면 false 반환 (남아있기)
        },
        customActionButton: (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="basic"
              size="large"
              onClick={() => {
                closeModal();
                track.onClick({
                  clickEventName: ClickEventName.click_newgame_ai_exit_modal_exit,
                  params: {
                    channel_id: appId
                  }
                });
                clearSessionStorage();
                resolve(true); // 나가기 버튼 클릭 시 true 반환
              }}
            >
              나가기
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                track.onClick({
                  clickEventName: ClickEventName.click_newgame_ai_exit_modal_cancel,
                  params: {
                    channel_id: appId
                  }
                });
                closeModal();
                resolve(false); // 남아있기 버튼 클릭 시 false 반환
              }}
            >
              남아있기
            </Button>
          </div>
        )
      });
    });
  }, [openModal, closeModal, appId, clearSessionStorage]);

  // 페이지 이탈 시 실행할 로직
  const { allowExit } = usePageExit(
    () => {
      clearSessionStorage();
    },
    {
      onBeforeUnload: () => console.log('새로고침/브라우저 종료'),
      onVisibilityChange: () => console.log('탭 변경/브라우저 숨김'),
      onRouteChange: () => console.log('다른 페이지로 라우팅'),
      shouldPreventDefault: true,
      shouldBlockRouting: true,
      enabled: true,
      customConfirmModal: openUnsavedChangesWarningModal,
      exitPath: `/apps/${appId}`
    }
  );

  const handleContentChange = (
    section: Exclude<keyof StoryFormData, 'aiUseLogId'>,
    content: string
  ) => {
    setValue(section, content);
  };

  const handleTitleChange = (title: string) => {
    // 최대 100글자로 제한
    if (title.length <= 100) {
      setValue('title', title);
      setTitleLength(title.length);
      // 제목이 비어있는지 검사 (공백만 있어도 비어있는 것으로 간주)
      setIsTitleEmpty(title.trim() === '');
    }
  };

  // 섹션 편집 상태 변경 핸들러
  const handleSectionEditModeChange = useCallback((section: string, isEditing: boolean) => {
    setEditingSections((prev) => {
      const newSet = new Set(prev);
      if (isEditing) {
        newSet.add(section);
      } else {
        newSet.delete(section);
      }
      return newSet;
    });
  }, []);

  // 각 섹션별 편집 상태 변경 핸들러 (안정적인 참조 유지)
  const handleIntroductionEditModeChange = useCallback(
    (isEditing: boolean) => {
      handleSectionEditModeChange('introduction', isEditing);
    },
    [handleSectionEditModeChange]
  );

  const handleDevelopmentEditModeChange = useCallback(
    (isEditing: boolean) => {
      handleSectionEditModeChange('development', isEditing);
    },
    [handleSectionEditModeChange]
  );

  const handleConclusionEditModeChange = useCallback(
    (isEditing: boolean) => {
      handleSectionEditModeChange('conclusion', isEditing);
    },
    [handleSectionEditModeChange]
  );

  // 편집 중인 섹션이 있는지 확인
  const hasEditingSections = editingSections.size > 0;

  const handleSave = (section: Exclude<keyof StoryFormData, 'aiUseLogId'>) => {
    const updatedValue = getValues(section);

    // SessionStorage에 업데이트된 데이터를 저장
    if (sessionStoryData) {
      const updatedSessionData: SessionStoryData = {
        ...sessionStoryData,
        data: {
          ...sessionStoryData.data,
          [section]: updatedValue
        }
      };
      sessionStorage.setItem('aiStory', JSON.stringify(updatedSessionData));
    }
  };

  // 전체 폼 제출 핸들러
  const onSubmit = (data: StoryFormData) => {
    // 제출 시에는 의도적인 페이지 이동이므로 allowExit을 호출하여 페이지 이탈 허용
    allowExit();
    // SessionStorage에 전체 업데이트된 데이터를 저장
    if (sessionStoryData) {
      const updatedSessionData: SessionStoryData = {
        ...sessionStoryData,
        data: {
          title: data.title,
          introduction: data.introduction,
          development: data.development,
          conclusion: data.conclusion,
          aiUseLogId: data.aiUseLogId
        }
      };
      sessionStorage.setItem('aiStory', JSON.stringify(updatedSessionData));
      history.push(`/apps/${appId}/ai/styleConfig`);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Layout
        title="스토리를 완성해볼까요?"
        description="원하는 부분을 수정할 수 있어요"
        progress={66.66}
      >
        <div css={Container}>
          {/* SessionStorage에 데이터가 없을 때 메시지 표시 */}
          {!sessionStoryData && (
            <div css={NoDataMessage}>
              <p>스토리 데이터가 없습니다.</p>
              <p>먼저 스토리를 생성해주세요.</p>
            </div>
          )}

          {/* SessionStorage에 데이터가 있을 때만 폼 표시 */}
          {sessionStoryData && (
            <>
              {/* 제목 입력 영역 */}
              <div css={TitleCard}>
                <div css={TitleHeader}>
                  <h3 css={TitleLabel}>제목</h3>
                  <div css={InputContainer}>
                    <input
                      css={[TitleInput, isTitleEmpty && TitleInputError]}
                      {...register('title')}
                      value={watchedValues.title || ''}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="스토리 제목을 입력하세요"
                      maxLength={100}
                    />
                    <span css={CharCount}>{titleLength}/100</span>
                  </div>
                </div>
              </div>

              {/* 스토리 섹션들 */}
              <div css={CardsContainer}>
                <StoryEditCard
                  title="도입"
                  type="introduction"
                  content={watchedValues.introduction || ''}
                  onContentChange={(content) => handleContentChange('introduction', content)}
                  onSave={() => handleSave('introduction')}
                  maxLength={500}
                  onEditModeChange={handleIntroductionEditModeChange}
                />

                <StoryEditCard
                  title="전개"
                  type="development"
                  content={watchedValues.development || ''}
                  onContentChange={(content) => handleContentChange('development', content)}
                  onSave={() => handleSave('development')}
                  maxLength={500}
                  onEditModeChange={handleDevelopmentEditModeChange}
                />

                <StoryEditCard
                  title="결말"
                  type="conclusion"
                  content={watchedValues.conclusion || ''}
                  onContentChange={(content) => handleContentChange('conclusion', content)}
                  onSave={() => handleSave('conclusion')}
                  maxLength={500}
                  onEditModeChange={handleConclusionEditModeChange}
                />
              </div>

              {/* 폼 제출 버튼 - 편집 중인 섹션이 없어야 표시 */}
              {!hasEditingSections && (
                <div css={ButtonContainer}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      track.onClick({
                        clickEventName: ClickEventName.click_newgame_ai_storyconfirm_finish,
                        params: {
                          channel_id: appId,
                          title: sessionStoryData?.data.title,
                          introduction: sessionStoryData?.data.introduction,
                          development: sessionStoryData?.data.development,
                          conclusion: sessionStoryData?.data.conclusion
                        }
                      });
                      handleSubmit(onSubmit)();
                    }}
                  >
                    스토리 완성하기
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        <Modal {...modal} />
      </Layout>
    </form>
  );
}

export default AiStoryConfirm;

const Container = css`
  max-width: 650px;
  margin: 0 auto;
`;

const CardsContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const TitleCard = css`
  background-color: #ffffff;
  border-radius: 16px;
  border: 1px solid #ededed;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.07);
`;

const TitleHeader = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const InputContainer = css`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
`;

const TitleLabel = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #0e1017;
  margin: 0;
  white-space: nowrap;
`;

const TitleInput = css`
  flex: 1;
  background-color: #fafafa;
  border: 1px solid #ededed;
  border-radius: 8px;
  padding: 8px 12px;
  padding-right: 60px; /* 글자수 표시 영역 확보 */
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #191919;
  word-wrap: break-word; /* 긴 단어 자동 줄바꿈 */
  overflow-wrap: break-word; /* CSS 표준 속성 */
  word-break: break-word; /* 긴 단어 강제 줄바꿈 */

  &::placeholder {
    color: #626262;
    opacity: 0.6;
  }

  &:focus {
    outline: none;
    border-color: #c8adff;
  }
`;

const CharCount = css`
  position: absolute;
  right: 12px;
  font-family: 'Pretendard', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #626262;
  pointer-events: none;
  background-color: #fafafa;
  padding-left: 4px;
`;

const TitleInputError = css`
  border-color: #e56767 !important;
`;

const ButtonContainer = css`
  display: flex;
  justify-content: center;
  margin: 32px 0 24px 0;
`;

const NoDataMessage = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;

  p {
    margin: 8px 0;
    font-family: 'Pretendard', sans-serif;
    color: #6c757d;
    font-size: 16px;
    font-weight: 400;
  }

  p:first-of-type {
    font-size: 18px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 12px;
  }
`;
